import useSWR from 'swr';
import useCmsQuery from '../../../api/hooks/useCmsQuery';
import { useFetcher } from '../../../api/swr';
import { getDepositMessages, getPnpCashierContent, TDepositMessages, TPnpCashierContent } from '../../../cms';
import useMarketInfo from '../../../hooks/useMarketInfo';
import { API_URL } from '../../../utils/envConstants';
import { FALLBACK_CONTENT } from '../pnpCashier.config';
import type { PnpCashierContent, TrustlyLimits, TrustlyPaymentMethod } from '../pnpCashier.types';

const withLimitPlaceholder = (msg: string, key: 'min' | 'max'): string => {
  const placeholder = new RegExp(`\\{\\s*${key}\\s*\\}`);

  return placeholder.test(msg) ? msg : `${msg} {${key}}`;
};

const applyFallbacks = (
  content?: TPnpCashierContent,
  messages?: TDepositMessages,
): PnpCashierContent => ({
  header:           content?.header ?? FALLBACK_CONTENT.header,
  title:            content?.title ?? FALLBACK_CONTENT.title,
  description:      content?.description ?? FALLBACK_CONTENT.description,
  suggestedAmounts: content?.suggestedAmounts?.length
    ? content.suggestedAmounts
    : FALLBACK_CONTENT.suggestedAmounts,
  ctaLabel:    content?.ctaLabel ?? FALLBACK_CONTENT.ctaLabel,
  placeholder: content?.placeholder ?? FALLBACK_CONTENT.placeholder,
  termsText:   content?.termsText ?? FALLBACK_CONTENT.termsText,
  errorMin:    messages?.tooLow
    ? withLimitPlaceholder(messages.tooLow, 'min')
    : FALLBACK_CONTENT.errorMin,
  errorMax: messages?.tooHigh
    ? withLimitPlaceholder(messages.tooHigh, 'max')
    : FALLBACK_CONTENT.errorMax,
  errorInvalid: messages?.required ?? FALLBACK_CONTENT.errorInvalid,
});

export const usePnpCashierContent = () => {
  const { language } = useMarketInfo();

  const { data: content, loading: contentLoading } = useCmsQuery(
    () => getPnpCashierContent(language),
    [language],
  );

  const { data: messages, loading: messagesLoading } = useCmsQuery(
    () => getDepositMessages(language),
    [language],
  );

  return {
    content:   applyFallbacks(content, messages),
    isLoading: contentLoading || messagesLoading,
  };
};

export const usePnpLimits = () => {
  const { marketCode, market } = useMarketInfo();
  const fetcher = useFetcher();
  const marketParam = marketCode ?? market;

  const { data, isLoading, error } = useSWR(
    `${API_URL}/casino-player/payment-methods/api/pay-and-play/${encodeURIComponent(marketParam)}`,
    fetcher,
    { revalidateIfStale: false },
  );

  let limits: TrustlyLimits | null = null;

  if (data && Array.isArray(data)) {
    const trustly = (data as TrustlyPaymentMethod[]).find(
      (m) => m.name.toUpperCase() === 'TRUSTLY',
    );

    if (trustly) {
      limits = {
        minAmount: trustly.limits.minAmount.amount,
        maxAmount: trustly.limits.maxAmount.amount,
        currency:  trustly.limits.minAmount.iso4217CurrencyCode,
      };
    }
  }

  return { limits, isLoading, error };
};
